import StaticPageLayout from 'components/StaticPageLayout'
import InternalLink from 'components/UI/InternalLink'
import ComponentFAQ from 'components/directus/FAQ'
import EmbeddedVideo from 'components/features-page/EmbeddedVideo'
import FeatureHeader from 'components/features-page/FeatureHeader'
import { AboutTheFeature, GridItems } from 'components/features-page/FeaturePageSkeleton'
import FeatureSummary from 'components/features-page/FeatureSummary'
import { graphql } from 'gatsby'
import NetworkOptionsFeatureImage from 'images/network-options-image.png'
import { ReactElement } from 'react'
import { Flex } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import { useFeaturePageSlug } from 'utils/useFeaturePageSlug'

export default function NetworkOptions({
  data,
}: {
  data: Queries.NetworkOptionsPageQuery
}): ReactElement {
  const networkOptionsData = getEnglishStrings(data.directus.network_options?.strings)
  const generalTranslationsData = getEnglishStrings(data.directus.general_translations?.strings)
  const queryParam = useFeaturePageSlug()
  return (
    <StaticPageLayout
      title={`${networkOptionsData?.title ?? ''} | Windscribe`}
      metaDescription={networkOptionsData?.meta_description ?? ''}
      metaKeywords={networkOptionsData?.meta_keywords ?? []}
      ctaQueryParams={queryParam}
    >
      <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: 'default', mb: 'default' }}>
        <FeatureHeader
          title={networkOptionsData?.title ?? ''}
          subtitle={networkOptionsData?.description ?? ''}
          imageSource={NetworkOptionsFeatureImage}
        />
        <FeatureSummary
          text={networkOptionsData?.summary_text ?? ''}
          summaryTranslation={generalTranslationsData?.summary ?? ''}
        />
        <EmbeddedVideo
          videoTitle={networkOptionsData?.video_title ?? ''}
          videoSrc="https://www.youtube-nocookie.com/embed/H1BvU7QtrNQ?si=TeW_KmXnKBWLpsU-"
        />
        <GridItems
          cta={
            <InternalLink href={`/download?${queryParam}`} sx={{ variant: 'buttons.neon' }}>
              {networkOptionsData?.cta}
            </InternalLink>
          }
          gridItems={[
            {
              title: networkOptionsData?.custom_protocol_title ?? '',
              description: networkOptionsData?.custom_protocol_description ?? '',
            },
            {
              title: networkOptionsData?.network_whitelisting_title ?? '',
              description: networkOptionsData?.network_whitelisting_description ?? '',
            },
            {
              title: networkOptionsData?.connect_automatically_title ?? '',
              description: networkOptionsData?.connect_automatically_description ?? '',
            },
            {
              title: networkOptionsData?.seamless_transitions ?? '',
              description: networkOptionsData?.seamless_transitions_description ?? '',
            },
          ]}
        />
        <AboutTheFeature
          aboutTheFeature={[
            {
              title: generalTranslationsData?.by_the_way ?? '',
              items: [networkOptionsData?.tldr_text ?? ''],
            },
          ]}
        />

        <ComponentFAQ
          data={data.directus?.network_options?.faq as Queries.DirectusData_component_faq}
          sxx={{ maxWidth: 'maxContentWidth' }}
        />
      </Flex>
    </StaticPageLayout>
  )
}

export const data = graphql`
  query NetworkOptionsPage {
    directus {
      network_options {
        strings {
          connect_automatically_description
          custom_protocol_description
          connect_automatically_title
          custom_protocol_title
          description
          languages_code {
            code
          }
          network_whitelisting_description
          network_whitelisting_title
          seamless_transitions
          seamless_transitions_description
          summary_text
          title
          tldr_text
          meta_description
          meta_keywords
          cta
          video_title
        }
        faq {
          items {
            component_faq_id {
              id
            }
            faq_items_id {
              strings {
                languages_code {
                  code
                }
                question
                answer
              }
            }
          }
        }
      }
      general_translations {
        strings {
          languages_code {
            code
          }
          summary
          by_the_way
        }
      }
    }
  }
`
